import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface ILanguageSelectorItem {
  code: string;
  label: string;
}

/** Fallback used before any detection runs and whenever a stored code is unknown. */
const DEFAULT_LANG: ILanguageSelectorItem = { code: 'en', label: 'English' };

/**
 * Key under which the chosen language is persisted.
 *
 * Note this deliberately uses raw `localStorage` rather than `StorageService`:
 * `StorageService.reset()` clears the whole store on logout and on account
 * deletion, and the superadmin impersonation flow swaps the entire vault. The
 * language preference is a device setting, not account data, and must survive
 * all three.
 */
const LANG_STORAGE_KEY = 'langCode';

/**
 * Central place for the active UI language.
 *
 * Mirrors the I18nService in the sibling `hai-app` repo so that code (and
 * translation keys) can move between the two projects unchanged.
 */
@Injectable({
  providedIn: 'root'
})
export class I18nService {

  /**
   * Labels are endonyms on purpose: a language picker should be readable by
   * someone who cannot yet read the current UI language.
   */
  private _languageSelectorItems = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'sv', label: 'Svenska' },
    { code: 'es', label: 'Español' },
  ] as ILanguageSelectorItem[];

  private _currLang$ = new BehaviorSubject<ILanguageSelectorItem>(DEFAULT_LANG);

  constructor(
    private readonly translate: TranslateService
  ) {
    // Resolve only — deliberately no `translate.use()` here. See init().
    const lang = this.resolveStoredLang() ?? this.resolveDeviceLang();
    if (lang) { this._currLang$.next(lang); }
  }

  /**
   * Applies the resolved language and waits for its file to load.
   *
   * This is separate from the constructor because of a trap in ngx-translate:
   * `use()` sets `currentLang` optimistically the moment it is called, and a
   * later `use()` for that same language hits the `lang === currentLang`
   * early-return, which resolves immediately and emits no `onLangChange`.
   *
   * With the call in the constructor, CoreProvider's `await use(code)` was
   * therefore a no-op wait: the app could paint — and every `instant()` during
   * boot could resolve — before the locale file had arrived. Doing it once,
   * here, and awaiting it is what actually makes the first render correct.
   *
   * A failure must not block boot; ngx-translate falls back to the key, which
   * is the English source string.
   */
  async init(): Promise<void> {
    const code = this._currLang$.value.code;
    try {
      this.translate.setDefaultLang(DEFAULT_LANG.code);
      await firstValueFrom(this.translate.use(code));

      // Partially translated locales fall back key by key, so the English table
      // has to be resident too.
      if (code !== DEFAULT_LANG.code && !this.translate.translations[DEFAULT_LANG.code]) {
        await firstValueFrom(this.translate.getTranslation(DEFAULT_LANG.code));
      }
    } catch (error) {
      console.error('Error loading translations', error);
    }
    this.syncDocumentLang(code);
  }

  /**
   * Async translation helper.
   *
   * `skipLangs` lets a caller keep the original English wording for specific
   * languages, which is occasionally needed for domain terms that should not be
   * localised in a given market.
   */
  tg(def: string, skipLangs: string[] = []): Promise<string> {
    return (skipLangs.includes(this._currLang$.value.code))
      ? Promise.resolve(def)
      : this.translate.get(def).toPromise() as Promise<string>
    ;
  }

  get currLang$(): BehaviorSubject<ILanguageSelectorItem> {
    return this._currLang$;
  }

  set curLangCode(value: ILanguageSelectorItem) {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, value.code);
    } catch (e) {
      // Private browsing / storage disabled: honour the choice for this session anyway.
      console.log(e);
    }
    this.translate.use(value.code);
    this._currLang$.next(value);
    this.syncDocumentLang(value.code);
  }

  get languageSelectorItems(): ILanguageSelectorItem[] {
    return this._languageSelectorItems;
  }

  /** Previously chosen language, if it is still one we support. */
  private resolveStoredLang(): ILanguageSelectorItem | undefined {
    try {
      const langCode = localStorage.getItem(LANG_STORAGE_KEY);
      return this._languageSelectorItems.find(itm => itm.code === langCode);
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }

  /**
   * First-launch detection from the device locale. Matches on the primary
   * subtag so that `de-AT`, `sv-FI` and `es-419` all resolve correctly.
   */
  private resolveDeviceLang(): ILanguageSelectorItem | undefined {
    try {
      const candidates = navigator.languages?.length
        ? navigator.languages
        : [navigator.language];

      for (const tag of candidates) {
        const primary = tag?.split('-')[0]?.toLowerCase();
        const match = this._languageSelectorItems.find(itm => itm.code === primary);
        if (match) { return match; }
      }
    } catch (e) {
      console.log(e);
    }
    return undefined;
  }

  /** Keeps `<html lang>` accurate for screen readers and browser translation prompts. */
  private syncDocumentLang(code: string): void {
    try {
      document.documentElement.setAttribute('lang', code);
    } catch (e) {
      console.log(e);
    }
  }
}
